import { useImageStatus } from '@versports/ui'
import { ClubMark } from '../../components/ClubMark'

/**
 * Una `logo_url` es "de otro origen" si es absoluta y su host no es el de la
 * app. Importa porque el marcador del estadio (`vr-app/src/ui/StadiumScreen.tsx`)
 * pinta el escudo en un canvas que alimenta una textura WebGL y por eso carga
 * la imagen con `crossOrigin="anonymous"`: sin `Access-Control-Allow-Origin`
 * del otro servidor la carga falla y en la cancha se ve el escudo dibujado en
 * vez del logo real, aunque en el panel se vea perfecto.
 */
export function isCrossOriginLogo(value: string, origin: string): boolean {
  const src = value.trim()
  if (!src || src.startsWith('data:')) return false
  try {
    return new URL(src, origin).origin !== new URL(origin).origin
  } catch {
    return false
  }
}

/** Lado del preview en px: tiene que alcanzar para juzgar el escudo, no sólo confirmar que hay algo. */
const PREVIEW_SIZE = 96

export type ClubLogoFieldProps = {
  value: string
  onChange: (value: string) => void
  /** Nombre del club: alimenta las iniciales del preview. */
  name: string
  /** Color de marca, para ver el placeholder tal cual va a salir. */
  fallbackColor?: string | null
  /** Inyectable para tests. Default `window.location.origin`. */
  origin?: string
}

/**
 * Campo "Logo (URL)" del panel de superadmin, con preview y diagnóstico.
 *
 * Por qué existe (gap e2e 8.10, 2026-09-07): "Club Prueba E2E" quedó guardado
 * con `https://ver-sports.com/logo.png`, que no es un archivo — nginx responde
 * el `index.html` de la SPA con 200 y `content-type: text/html`, así que el
 * browser la trata como imagen rota. `ClubMark` garantiza que eso nunca se vea
 * como el glifo roto, pero justamente por eso el operador no se entera de que
 * cargó una URL muerta: guarda, ve el placeholder de iniciales y cree que el
 * club no tiene logo. Acá el estado de la carga se dice explícito.
 */
export function ClubLogoField({
  value,
  onChange,
  name,
  fallbackColor,
  origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
}: ClubLogoFieldProps) {
  const src = value.trim()
  const status = useImageStatus(src || null)
  const crossOrigin = isCrossOriginLogo(src, origin)

  return (
    <label className="flex flex-col gap-xs text-sm">
      Logo (URL)
      <div className="flex flex-col gap-md sm:flex-row sm:items-start">
        {/* El escudo es lo que el operador tiene que verificar a ojo (que sea
            el del club y que no venga recortado o con fondo opaco), así que va
            grande y en su propio marco; a 40px al lado del input no se
            distinguía nada. */}
        <span className="inline-flex shrink-0 items-center justify-center rounded-md border border-outline bg-surface p-sm">
          <ClubMark
            logoUrl={src}
            name={name}
            size={PREVIEW_SIZE}
            radius={6}
            fallbackColor={fallbackColor}
          />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-xs">
          <input
            type="text"
            className="input"
            placeholder="https://…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {src && status === 'loading' && (
            <span className="text-xs text-text-muted">Cargando…</span>
          )}
          {src && status === 'failed' && (
            <span className="text-xs text-danger">
              Esa URL no devuelve una imagen. Se va a mostrar el placeholder con las iniciales. Ojo
              con las rutas de la propia app (por ejemplo <code>/logo.png</code>): responden el HTML
              del sitio con 200, no un archivo.
            </span>
          )}
          {src && status === 'loaded' && crossOrigin && (
            <span className="text-xs text-text-muted">
              Carga bien acá, pero es de otro dominio: el marcador del estadio necesita CORS
              (<code>Access-Control-Allow-Origin</code>) o un logo servido desde este mismo sitio, si
              no en la cancha se ve el escudo dibujado.
            </span>
          )}
          {src && status === 'loaded' && !crossOrigin && (
            <span className="text-xs text-text-muted">Se ve así en el panel y en la cancha.</span>
          )}
          {!src && (
            <span className="text-xs text-text-muted">
              Sin logo: en todos lados se ven las iniciales del club.
            </span>
          )}
        </div>
      </div>
    </label>
  )
}
